import { injectContentsquareScript } from '@contentsquare/tag-sdk'

import { useStationStore } from '@/lib/stores/station'

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

export type AnalyticsArea = 'timeline' | 'requests' | 'schedule' | 'navigation'
export type AnalyticsOutcome = 'attempted' | 'succeeded' | 'failed'

type StationParameter = { station_code?: string }
type OutcomeParameters = StationParameter & {
  outcome: AnalyticsOutcome
  failure_type?: 'validation' | 'api'
}

export type AnalyticsEventParameterMap = {
  request_assigned: OutcomeParameters & {
    area: 'timeline' | 'requests'
    assignment_method?: 'drag_drop' | 'modal'
  }
  request_reassigned: OutcomeParameters & {
    area: 'timeline' | 'requests'
    assignment_method?: 'drag_drop' | 'modal'
  }
  request_unassigned: OutcomeParameters & { area: 'timeline' | 'requests' }
  request_timing_updated: OutcomeParameters & {
    area: 'timeline'
    interaction_method?: 'drag_drop' | 'modal'
  }
  break_created: OutcomeParameters & { area: 'timeline' }
  break_updated: OutcomeParameters & { area: 'timeline' }
  break_deleted: OutcomeParameters & { area: 'timeline' }
  note_added: OutcomeParameters & { area: 'timeline' | 'requests' }
  checklist_started: StationParameter & { area: 'timeline' | 'requests' }
  checklist_completed: OutcomeParameters & { area: 'timeline' | 'requests' }
  email_sent: OutcomeParameters & { area: 'timeline' | 'requests' }
  schedule_saved: OutcomeParameters & {
    area: 'schedule'
    schedule_type?: 'baseline' | 'override'
  }
  schedule_removed: OutcomeParameters & {
    area: 'schedule'
    schedule_type?: 'baseline' | 'override'
  }
  request_opened: StationParameter & { area: 'timeline' }
  station_selected: StationParameter & { area: 'navigation' }
  timeline_searched: StationParameter & { area: 'timeline' }
  filter_applied: StationParameter & {
    area: 'timeline' | 'requests'
    filter_type: 'search' | 'preset' | 'category'
  }
  callsign_visibility_changed: StationParameter & {
    area: 'timeline'
    callsign_visibility: 'on_shift' | 'all'
  }
  agenda_opened: StationParameter & { area: 'schedule' }
  timeline_jump_to_now: StationParameter & { area: 'timeline' }
  navigation_selected: StationParameter & {
    area: 'navigation'
    navigation_target:
      | 'timeline'
      | 'requests'
      | 'shifts'
      | 'configuration'
      | 'calendar'
      | 'default_schedule'
      | 'schedule_override'
      | 'activity_log'
  }
}

export type AnalyticsEventName = keyof AnalyticsEventParameterMap
export type AnalyticsEventParameters = AnalyticsEventParameterMap[AnalyticsEventName]
export type AnalyticsEventParametersFor<TEvent extends AnalyticsEventName> =
  AnalyticsEventParameterMap[TEvent]
export type AnalyticsOutcomeEventName = {
  [TEvent in AnalyticsEventName]: AnalyticsEventParameterMap[TEvent] extends {
    outcome: AnalyticsOutcome
  }
    ? TEvent
    : never
}[AnalyticsEventName]

const gtag: (...args: unknown[]) => void = function () {
  // Google treats Arguments objects as commands; plain arrays are ignored.
  window.dataLayer.push(arguments)
}

const runAnalyticsSafely = (operation: () => void) => {
  try {
    operation()
  } catch {
    // Analytics must never interrupt app behaviour.
  }
}

export const initializeGoogleAnalytics = (measurementId: string) =>
  runAnalyticsSafely(() => {
    window.dataLayer = window.dataLayer || []
    window.gtag = gtag
    gtag('js', new Date())
    // PageViewTracker sends the initial view and subsequent route changes.
    gtag('config', measurementId, { send_page_view: false })

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
    document.head.append(script)
  })

export const initializeContentsquare = (tagId: string) =>
  runAnalyticsSafely(() => injectContentsquareScript({ clientId: tagId }))

export const trackEvent = <TEvent extends AnalyticsEventName>(
  name: TEvent,
  parameters: AnalyticsEventParametersFor<TEvent>
) => {
  try {
    const { station_code = useStationStore.getState().selectedStationCode, ...eventParameters } =
      parameters
    window.gtag?.('event', name, {
      ...eventParameters,
      ...(station_code ? { station_code } : {}),
    })
    // Mutation callbacks reuse this snapshot if the selected station changes before the response.
    return { station_code }
  } catch {
    // Analytics must never interrupt app behaviour.
    return {}
  }
}

export const trackOutcome = <TEvent extends AnalyticsOutcomeEventName>(
  name: TEvent,
  area: AnalyticsEventParametersFor<TEvent>['area'],
  outcome: AnalyticsOutcome,
  parameters?: Omit<AnalyticsEventParametersFor<TEvent>, 'area' | 'outcome'>
) => trackEvent(name, { area, outcome, ...parameters } as AnalyticsEventParametersFor<TEvent>)

export const trackPageView = (pathname: string, title: string) => {
  runAnalyticsSafely(() => {
    // The sign-in redirect is not a product workspace.
    if (pathname === '/callback') return

    const stationCode = useStationStore.getState().selectedStationCode
    window.gtag?.('event', 'page_view', {
      page_path: pathname,
      page_location: `${window.location.origin}${pathname}`,
      page_title: title,
      ...(stationCode ? { station_code: stationCode } : {}),
    })
  })
}
